import type {
  AuthGateway,
  AuthenticatedSession,
  PrimarySignInResult,
  SafeAuthErrorCode,
} from "./auth-types.ts";
import { SafeAuthError } from "./auth-types.ts";
import { normalizeMfaDigits } from "./mfa-code.ts";

export type AuthFlowState =
  | { readonly view: "mfa-enrollment-success" }
  | { readonly view: "initializing" }
  | {
      readonly view: "sign-in";
      readonly busy: boolean;
      readonly errorCode: SafeAuthErrorCode | null;
      readonly notice: string | null;
    }
  | {
      readonly view: "mfa-challenge";
      readonly busy: boolean;
      readonly errorCode: SafeAuthErrorCode | null;
    }
  | {
      readonly view: "mfa-enrollment";
      readonly busy: boolean;
      readonly errorCode: SafeAuthErrorCode | null;
      readonly accountLabel: string;
      readonly secretKey: string;
      readonly setupUrl: string;
    }
  | { readonly view: "authenticated"; readonly session: AuthenticatedSession };

const INITIAL_SIGN_IN_STATE: AuthFlowState = Object.freeze({
  view: "sign-in",
  busy: false,
  errorCode: null,
  notice: null,
});

function safeCode(error: unknown): SafeAuthErrorCode {
  return error instanceof SafeAuthError ? error.code : "unexpected-error";
}

export class AuthFlowController {
  #state: AuthFlowState = Object.freeze({ view: "initializing" });
  #restorePromise: Promise<void> | null = null;
  readonly #listeners = new Set<() => void>();
  readonly #gateway: AuthGateway;

  constructor(gateway: AuthGateway) {
    this.#gateway = gateway;
  }

  readonly getSnapshot = () => this.#state;

  readonly subscribe = (listener: () => void) => {
    this.#listeners.add(listener);
    return () => this.#listeners.delete(listener);
  };

  #publish(state: AuthFlowState) {
    this.#state = Object.freeze(state);
    for (const listener of this.#listeners) listener();
  }

  restore(): Promise<void> {
    if (this.#restorePromise) return this.#restorePromise;
    if (this.#state.view !== "initializing") return Promise.resolve();
    this.#restorePromise = this.#restoreOnce();
    return this.#restorePromise;
  }

  async #restoreOnce() {
    try {
      const restored = await this.#gateway.restore();
      this.#publish(
        restored.kind === "authenticated"
          ? { view: "authenticated", session: restored.session }
          : INITIAL_SIGN_IN_STATE,
      );
    } catch (error) {
      this.#publish({
        view: "sign-in",
        busy: false,
        errorCode: safeCode(error),
        notice: null,
      });
    }
  }

  async signInWithPassword(email: string, password: string) {
    if (this.#state.view !== "sign-in" || this.#state.busy) return;
    this.#publish({
      view: "sign-in",
      busy: true,
      errorCode: null,
      notice: null,
    });
    await this.#start(() => this.#gateway.signInWithPassword(email, password));
  }

  async signInWithGoogle() {
    if (this.#state.view !== "sign-in" || this.#state.busy) return;
    this.#publish({
      view: "sign-in",
      busy: true,
      errorCode: null,
      notice: null,
    });
    await this.#start(() => this.#gateway.signInWithGoogle());
  }

  async #start(operation: () => Promise<PrimarySignInResult>) {
    try {
      const result = await operation();
      if (result.kind === "authenticated") {
        this.#publish({ view: "authenticated", session: result.session });
      } else if (result.kind === "mfa-challenge") {
        this.#publish({
          view: "mfa-challenge",
          busy: false,
          errorCode: null,
        });
      } else {
        this.#publish({
          view: "mfa-enrollment",
          busy: false,
          errorCode: null,
          accountLabel: result.accountLabel,
          secretKey: result.secretKey,
          setupUrl: result.setupUrl,
        });
      }
    } catch (error) {
      this.#publish({
        view: "sign-in",
        busy: false,
        errorCode: safeCode(error),
        notice: null,
      });
    }
  }

  async verifyMfa(code: string) {
    if (this.#state.view !== "mfa-challenge" || this.#state.busy) return;
    this.#publish({ ...this.#state, busy: true, errorCode: null });
    try {
      const session = await this.#gateway.completeMfaChallenge(
        normalizeMfaDigits(code),
      );
      this.#publish({ view: "authenticated", session });
    } catch (error) {
      this.#publish({
        view: "mfa-challenge",
        busy: false,
        errorCode: safeCode(error),
      });
    }
  }

  async enrollMfa(code: string) {
    if (this.#state.view !== "mfa-enrollment" || this.#state.busy) return;
    const enrollment = this.#state;
    this.#publish({ ...enrollment, busy: true, errorCode: null });
    try {
      await this.#gateway.completeMfaEnrollment(normalizeMfaDigits(code));
      this.#publish({ view: "mfa-enrollment-success" });
    } catch (error) {
      this.#publish({
        ...enrollment,
        busy: false,
        errorCode: safeCode(error),
      });
    }
  }

  finishEnrollment() {
    if (this.#state.view !== "mfa-enrollment-success") return;
    this.#publish({
      view: "sign-in",
      busy: false,
      errorCode: null,
      notice: "MFA is configured. Sign in again to verify your code.",
    });
  }

  async cancelMfa() {
    if (
      this.#state.view !== "mfa-challenge" &&
      this.#state.view !== "mfa-enrollment"
    ) {
      return;
    }
    await this.#gateway.cancelMfa();
    this.#publish(INITIAL_SIGN_IN_STATE);
  }

  async signOut() {
    await this.#gateway.signOut();
    this.#publish(INITIAL_SIGN_IN_STATE);
  }
}

export const AUTH_ERROR_COPY: Readonly<Record<SafeAuthErrorCode, string>> =
  Object.freeze({
    "invalid-credentials": "The email or password is incorrect.",
    "account-disabled": "This account has been disabled.",
    "account-not-provisioned":
      "This Google account has not been provisioned for the dashboard.",
    "access-denied": "This account is not authorized for the dashboard.",
    "google-sign-in-unavailable":
      "Google sign-in could not open. Allow pop-ups or use standalone Chrome or Safari, then try again.",
    "invalid-mfa-code": "The verification code is invalid or has expired.",
    "network-unavailable": "The service could not be reached. Try again.",
    "popup-closed": "Google sign-in was cancelled.",
    "session-expired": "Your secure session expired. Sign in again.",
    "unexpected-error": "Sign-in could not be completed.",
  });
